import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Tpl3plService } from '../../../services/tpl3pl.service';

export interface SkuMismatchResolveDialogData {
	shipmentId: number;
	orderno: string;
	tpl3plService: Tpl3plService;
}

interface MappingRow {
	alias_sku: string;
	description: string;
	qty: number;
	in_stockmaster: boolean;
	canonical_sku: string;
	note: string;
}

@Component({
	selector: 'app-sku-mismatch-resolve-dialog',
	template: `
		<h2 mat-dialog-title>Resolve SKU mapping — order {{ data.orderno }}</h2>
		<mat-dialog-content class="smr-content">
			<mat-progress-bar *ngIf="loading" mode="indeterminate"></mat-progress-bar>
			<p *ngIf="error" class="smr-err">{{ error }}</p>

			<p class="smr-hint small text-muted" *ngIf="!loading && !error">
				PGS shipped the codes below, which aren't on this order. Map each to the matching open
				order line — it's saved as a reusable alias, then the shipment re-invoices the shipped
				quantity (any unshipped lines stay open). Future shipments under that code match automatically.
			</p>

			<table class="smr-table" *ngIf="!loading && rows.length > 0">
				<thead>
					<tr>
						<th>Shipped code (PGS)</th>
						<th class="smr-qty">Qty</th>
						<th>Maps to open order line</th>
						<th>Note</th>
					</tr>
				</thead>
				<tbody>
					<tr *ngFor="let r of rows">
						<td>
							<strong>{{ r.alias_sku }}</strong>
							<div class="smr-desc">{{ r.description || (r.in_stockmaster ? '' : 'not in stockmaster') }}</div>
						</td>
						<td class="smr-qty">{{ r.qty }}</td>
						<td>
							<input
								class="smr-input"
								type="text"
								[(ngModel)]="r.canonical_sku"
								[attr.list]="datalistId"
								placeholder="Choose / type open line code"
								autocomplete="off"
								spellcheck="false"
							/>
						</td>
						<td>
							<input class="smr-input smr-note-input" type="text" [(ngModel)]="r.note" placeholder="optional" autocomplete="off" />
						</td>
					</tr>
				</tbody>
			</table>

			<datalist [id]="datalistId">
				<option *ngFor="let ol of openLines" [value]="ol.stkcode">{{ ol.description }} (rem {{ ol.remaining }})</option>
			</datalist>

			<p *ngIf="!loading && !error && rows.length === 0" class="smr-empty">
				All shipped codes already match the order (covered by existing aliases) — no new mapping needed.
				Use <strong>Re-invoice now</strong> to clear this parked shipment.
			</p>

			<p *ngIf="reinvoiceMsg" class="smr-reinvoice" [class.smr-ok]="reinvoiceOk" [class.smr-warn]="!reinvoiceOk">
				{{ reinvoiceMsg }}
			</p>
		</mat-dialog-content>
		<mat-dialog-actions align="end">
			<button mat-button type="button" mat-dialog-close>Close</button>
			<button
				mat-flat-button
				color="primary"
				type="button"
				*ngIf="!loading && !error"
				[disabled]="saving || primaryDisabled()"
				(click)="save()"
			>
				{{ saving ? 'Working…' : primaryLabel() }}
			</button>
		</mat-dialog-actions>
	`,
	styles: [`
		.smr-content { min-width: 640px; max-width: 860px; }
		.smr-hint { margin: 4px 0 12px; max-width: 760px; }
		.smr-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; }
		.smr-table th, .smr-table td { padding: 6px 8px; border-bottom: 1px solid #eee; text-align: left; vertical-align: top; }
		.smr-qty { text-align: right; width: 56px; }
		.smr-desc { color: #777; font-size: 0.78rem; margin-top: 2px; }
		.smr-input {
			width: 100%;
			min-width: 240px;
			box-sizing: border-box;
			padding: 7px 9px;
			font-size: 0.88rem;
			border: 1px solid #bdbdbd;
			border-radius: 4px;
			background: #fff;
		}
		.smr-input:focus { outline: none; border-color: #1976d2; box-shadow: 0 0 0 1px #1976d2; }
		.smr-note-input { min-width: 140px; width: 160px; }
		.smr-err { color: #c62828; }
		.smr-empty { color: #777; margin: 12px 0; }
		.smr-reinvoice { margin-top: 10px; font-size: 0.88rem; }
		.smr-ok { color: #2e7d32; }
		.smr-warn { color: #e65100; }
	`]
})
export class SkuMismatchResolveDialogComponent implements OnInit {
	loading = true;
	saving = false;
	error = '';
	rows: MappingRow[] = [];
	openLines: Array<{ stkcode: string; description: string; remaining: number }> = [];
	reinvoiceMsg = '';
	reinvoiceOk = false;
	/** Unique <datalist> id so the native dropdown binds per-dialog (avoids stale id reuse). */
	datalistId = 'smr-openlines';

	constructor(
		public dialogRef: MatDialogRef<SkuMismatchResolveDialogComponent>,
		@Inject(MAT_DIALOG_DATA) public data: SkuMismatchResolveDialogData
	) {}

	ngOnInit(): void {
		this.datalistId = 'smr-openlines-' + this.data.shipmentId;
		this.data.tpl3plService.getSkuMismatchDetail(this.data.shipmentId).subscribe({
			next: (res) => {
				this.loading = false;
				if (!res?.success) {
					this.error = res?.message || 'Failed to load mismatch detail';
					return;
				}
				this.openLines = res.open_lines || [];
				const suggestions = res.suggestions || {};
				this.rows = (res.shipped || [])
					.filter((s) => !s.matched)
					.map((s) => ({
						alias_sku: s.sku,
						description: s.description,
						qty: s.qty,
						in_stockmaster: s.in_stockmaster,
						canonical_sku: s.existing_alias || suggestions[s.sku] || '',
						note: ''
					}));
			},
			error: (err) => {
				this.loading = false;
				this.error = err?.error?.message || err?.message || 'Request failed';
			}
		});
	}

	hasChosenMapping(): boolean {
		return this.rows.some((r) => (r.canonical_sku || '').trim() !== '');
	}

	/** No unmatched rows = codes already covered by existing aliases; offer re-invoice only. */
	private reinvoiceOnly(): boolean {
		return this.rows.length === 0;
	}

	primaryLabel(): string {
		return this.reinvoiceOnly() ? 'Re-invoice now' : 'Save mapping & invoice';
	}

	primaryDisabled(): boolean {
		return this.reinvoiceOnly() ? false : !this.hasChosenMapping();
	}

	save(): void {
		const mappings = this.rows
			.filter((r) => (r.canonical_sku || '').trim() !== '')
			.map((r) => ({ alias_sku: r.alias_sku, canonical_sku: r.canonical_sku.trim(), note: r.note?.trim() || undefined }));
		// Empty mappings is valid only when there's nothing to map (re-invoice-only).
		if (mappings.length === 0 && !this.reinvoiceOnly()) {
			return;
		}
		this.saving = true;
		this.reinvoiceMsg = '';
		this.data.tpl3plService.saveSkuAliases(mappings, this.data.shipmentId, true).subscribe({
			next: (res) => {
				this.saving = false;
				if (!res?.success) {
					this.error = (res?.message || 'Save failed') + (res?.errors?.length ? ' — ' + res.errors.join('; ') : '');
					return;
				}
				if (res.reinvoice) {
					this.reinvoiceOk = !!res.reinvoice.success;
					this.reinvoiceMsg = res.reinvoice.message || (res.reinvoice.success ? 'Re-invoiced' : 'Re-invoice did not complete');
				}
				// Close shortly after so the operator sees the re-invoice outcome.
				this.dialogRef.close({ success: true, saved: res.saved, reinvoice: res.reinvoice, errors: res.errors });
			},
			error: (err) => {
				this.saving = false;
				this.error = err?.error?.message || err?.message || 'Request failed';
			}
		});
	}
}
